// ---------------------------------------------------------------------------
// useGraderTemplateGuidance — the grader-side adapter for the resource-agnostic
// template-reference detection in `utils/templateRefs`. Each grader-create hook
// describes its template-bearing text fields (a `field` key, a human `label`,
// the current `text`, and its `setText`); this hook returns the awareness slice
// the shared `TemplateVarCallouts` renders: undeclared `{{$var:…}}` names, bare
// `{{…}}` tokens, per-field labels, and a one-click bare→`$var` conversion.
// Declaring a variable is owned by the base spine (`declareVariable`).
// ---------------------------------------------------------------------------

import { GRADER_MODEL_VARIABLE } from '../../../services/grader.interface.js';
import type {
  BareTemplateToken,
  NamedText,
} from '../../../utils/templateRefs.js';
import {
  collectBareTemplateTokens,
  collectReferencedVars,
  replaceBareTokenWithVar,
} from '../../../utils/templateRefs.js';
import type { VariableRow } from '../../AgentsPage/AgentForm/AgentForm.types.js';
import { collectDeclaredVarNames } from '../../AgentsPage/AgentForm/AgentForm.utils.js';

/**
 * System-supplied variables the platform binds at evaluation time (e.g. the
 * grader's model). They are legitimate `{{$var:…}}` references that authors do
 * not declare in the Variables panel, so they must never be flagged as
 * undeclared.
 */
const IMPLICIT_VAR_NAMES = new Set<string>([GRADER_MODEL_VARIABLE]);

/** One template-bearing field a grader exposes for scanning + convert. */
export interface GraderTemplateField {
  field: string;
  label: string;
  text: string;
  setText: (value: string) => void;
}

export interface GraderTemplateGuidance {
  /** `$var` names referenced in the fields but not declared as variables. */
  undeclaredVars: string[];
  /** Bare `{{…}}` tokens found across the fields. */
  bareTokens: BareTemplateToken[];
  /** Human labels keyed by `field`, for the callout's "in <field>" note. */
  templateFieldLabels: Record<string, string>;
  /** Rewrite the bare token in its field as `{{$var:INNER}}`. */
  convertBareToken: (token: BareTemplateToken) => void;
}

export function useGraderTemplateGuidance(
  fields: GraderTemplateField[],
  variables: VariableRow[],
): GraderTemplateGuidance {
  const namedTexts: NamedText[] = fields.map(({ field, text }) => ({
    field,
    text,
  }));

  const declared = collectDeclaredVarNames(variables);
  const undeclaredVars = collectReferencedVars(namedTexts).filter(
    (name) => !declared.has(name) && !IMPLICIT_VAR_NAMES.has(name),
  );
  const bareTokens = collectBareTemplateTokens(namedTexts);

  const templateFieldLabels: Record<string, string> = {};
  for (const { field, label } of fields) templateFieldLabels[field] = label;

  const convertBareToken = (token: BareTemplateToken) => {
    const target = fields.find(({ field }) => field === token.field);
    if (target) target.setText(replaceBareTokenWithVar(target.text, token));
  };

  return { undeclaredVars, bareTokens, templateFieldLabels, convertBareToken };
}
